import {
  Button,
  Input,
  Message,
  SelectPicker,
  TagPicker,
  useToaster,
} from 'rsuite';
import React from 'react';

import * as studentsApi from '../../api/students';
import {
  FormField,
  FormGrid,
  FormModal,
  FormSection,
} from '../../common/FormModal';
import { useFormErrors } from '../../common/useFormErrors';

type Props = {
  open: boolean;
  setOpen: (newValue: boolean) => void;
  coursesData: { value: string; label: string }[];
  typesData: { value: number; label: string }[];
  callback: () => void;
};

export default function CreateStudentModal({
  open,
  setOpen,
  coursesData,
  typesData,
  callback,
}: Props) {
  const toaster = useToaster();
  const { errorSummary, setApiError, clearErrors, setErrorSummary } =
    useFormErrors({
      fallbackMessage: 'Не вдалося створити студента. Спробуйте пізніше',
      resetOn: [open],
    });
  const [firstName, setFirstName] = React.useState<string>('');
  const [secondName, setSecondName] = React.useState<string>('');
  const [type, setType] = React.useState<number>(0);
  const [email, setEmail] = React.useState<string>('');
  const [phone, setPhone] = React.useState<string>('');
  const [data, setData] = React.useState<string>('');
  const [selectedCoursesIds, setSelectedCoursesIds] = React.useState<string[]>(
    []
  );

  return (
    <FormModal
      open={open}
      onClose={() => setOpen(false)}
      title="Створити студента"
      errorSummary={errorSummary}
      footer={
        <>
          <Button
            onClick={async () => {
              clearErrors();
              try {
                const response = await studentsApi.create(
                  email,
                  type,
                  firstName,
                  secondName,
                  phone,
                  data,
                  selectedCoursesIds
                );
                if (response.alreadyExists) {
                  setErrorSummary(['Студент з такою поштою вже існує']);
                  toaster.push(
                    <Message type="error" showIcon closable>
                      Студент з такою поштою вже існує
                    </Message>,
                    {
                      placement: 'topEnd',
                      duration: 1500,
                    }
                  );
                  return;
                }
                toaster.push(
                  <Message type="success" showIcon closable>
                    Студента створено
                  </Message>,
                  {
                    placement: 'topEnd',
                    duration: 1500,
                  }
                );
                setOpen(false);
                setFirstName('');
                setSecondName('');
                setEmail('');
                setPhone('');
                setData('');
                callback();
              } catch (e: any) {
                console.error(e);
                const errorMessage = setApiError(e)[0];
                toaster.push(
                  <Message type="error" showIcon closable>
                    {errorMessage}
                  </Message>,
                  {
                    placement: 'topEnd',
                    duration: 1500,
                  }
                );
              }
            }}
            appearance="primary"
          >
            Створити
          </Button>
          <Button onClick={() => setOpen(false)} appearance="subtle">
            Скасувати
          </Button>
        </>
      }
    >
      <FormSection title="Основні дані">
        <FormGrid>
          <FormField label="Пошта" id="create-student-email" required>
            <Input
              value={email || ''}
              placeholder="Пошта"
              type="email"
              onChange={setEmail}
            />
          </FormField>
          <FormField label="Тип" id="create-student-type" required>
            <SelectPicker
              data={typesData}
              placeholder="Тип"
              searchable={false}
              value={type}
              onChange={value => {
                if (value === null) {
                  return;
                }
                setType(value);
              }}
            />
          </FormField>
        </FormGrid>
        <FormGrid>
          <FormField label="Ім'я" id="create-student-first-name" required>
            <Input
              value={firstName || ''}
              placeholder="Ім'я"
              onChange={setFirstName}
            />
          </FormField>
          <FormField label="Прізвище" id="create-student-second-name" required>
            <Input
              value={secondName || ''}
              placeholder="Прізвище"
              onChange={setSecondName}
            />
          </FormField>
        </FormGrid>
        <FormGrid>
          <FormField label="Телефон" id="create-student-phone" required>
            <Input
              value={phone || ''}
              placeholder="Телефон"
              onChange={setPhone}
            />
          </FormField>
          <FormField label="Коментар" id="create-student-comment">
            <Input
              as="textarea"
              rows={3}
              value={data || ''}
              placeholder="Коментар"
              onChange={setData}
            />
          </FormField>
        </FormGrid>
        <FormField label="Додати до черги курсів" id="create-student-courses">
          <TagPicker data={coursesData} onChange={setSelectedCoursesIds} />
        </FormField>
      </FormSection>
    </FormModal>
  );
}
